import React from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
import { Leaf, WheatOff } from "lucide-react";
type DietaryType = "V" | "GF";
interface MenuItem {
  name: string;
  ingredients: string;
  price: string;
  dietary?: DietaryType[];
}
interface MenuCategory {
  id: string;
  label: string;
  items: MenuItem[];
}
const menuData: MenuCategory[] = [{
  id: "forretter",
  label: "Forretter",
  items: [{
    name: "Salt-Cured Scallops",
    ingredients: "Hand-dived scallops, sea buckthorn, dill oil, frozen radish",
    price: "185",
    dietary: ["GF"]
  }, {
    name: "Beetroot Tartare",
    ingredients: "Smoked marrow, pickled mustard seeds, rye crumble, sorrel",
    price: "145",
    dietary: ["V"]
  }, {
    name: "Wild Mushroom Broth",
    ingredients: "Forest fungi, pine needle infusion, charred leek, lichen",
    price: "160",
    dietary: ["V", "GF"]
  }, {
    name: "Danish Coast Oyster",
    ingredients: "Cucumber granita, fresh horseradish, green apple, dill",
    price: "175",
    dietary: ["GF"]
  }, {
    name: "Smoked Venison",
    ingredients: "Wild lingonberries, juniper cream, fermented pear, moss",
    price: "195",
    dietary: ["GF"]
  }, {
    name: "Kohlrabi Carpaccio",
    ingredients: "Toasted hazelnut, brown butter, black truffle, chives",
    price: "155",
    dietary: ["V"]
  }]
}, {
  id: "hovedretter",
  label: "Hovedretter",
  items: [{
    name: "Cured Arctic Salmon",
    ingredients: "Glacier water brine, wild sorrel, radish, buttermilk",
    price: "285",
    dietary: ["GF"]
  }, {
    name: "Roasted Winter Cabbage",
    ingredients: "Fermented gooseberry, sunflower seeds, lovage oil",
    price: "245",
    dietary: ["V", "GF"]
  }, {
    name: "Turbot on the Bone",
    ingredients: "Brown shrimp, salt-marsh capers, cauliflower silk",
    price: "345",
    dietary: ["GF"]
  }, {
    name: "Grilled Langoustine",
    ingredients: "Birch sap glaze, charred gem lettuce, seaweed butter",
    price: "320",
    dietary: ["GF"]
  }, {
    name: "Slow-Cooked Pork Belly",
    ingredients: "Pickled rhubarb, wild ramsons, crispy crackling, jus",
    price: "295"
  }, {
    name: "Organic Barley Risotto",
    ingredients: "Aged Havarti, wild herbs, spring peas, lemon thyme",
    price: "265",
    dietary: ["V"]
  }]
}, {
  id: "signatur",
  label: "Signatur",
  items: [{
    name: "Aged Beef Rib",
    ingredients: "Black garlic, bone marrow jus, burnt onion, potato",
    price: "450",
    dietary: ["GF"]
  }, {
    name: "Deer Salami & Sourdough",
    ingredients: "Truffle honey, cultured butter, fermented grains",
    price: "380"
  }, {
    name: "Poached North Sea Cod",
    ingredients: "Seaweed butter, blue mussels, kale, oyster leaf",
    price: "410",
    dietary: ["GF"]
  }, {
    name: "Wild Duck Breast",
    ingredients: "Cherry reduction, roasted parsnip, mountain thyme",
    price: "435",
    dietary: ["GF"]
  }, {
    name: "Salted Jutland Lamb",
    ingredients: "Cabbage leaves, dill, sheep's milk yogurt, mint",
    price: "425",
    dietary: ["GF"]
  }, {
    name: "Forest Wild Boar",
    ingredients: "Root vegetables, juniper berries, red wine reduction",
    price: "460",
    dietary: ["GF"]
  }]
}, {
  id: "dessert",
  label: "Dessert",
  items: [{
    name: "Cloudberry Parfait",
    ingredients: "White chocolate, burnt honey, toasted almond",
    price: "135",
    dietary: ["V", "GF"]
  }, {
    name: "Rye Bread Ice Cream",
    ingredients: "Malt crumble, salted caramel, dark chocolate",
    price: "125",
    dietary: ["V"]
  }, {
    name: "Green Apple Sorbet",
    ingredients: "Cider reduction, lemon verbena, cucumber lace",
    price: "115",
    dietary: ["V", "GF"]
  }, {
    name: "70% Dark Chocolate",
    ingredients: "Sea salt, spruce tip oil, wild forest berries",
    price: "140",
    dietary: ["V", "GF"]
  }, {
    name: "Wild Blueberry Tart",
    ingredients: "Sheep's milk yogurt, lavender, honey tuile",
    price: "130",
    dietary: ["V"]
  }, {
    name: "Artisanal Nordic Cheese",
    ingredients: "Selection of aged cheeses, sourdough crispbread",
    price: "165",
    dietary: ["V"]
  }]
}];
const DietaryIcon = ({
  type
}: {
  type: DietaryType;
}) => {
  if (type === "V") {
    return <span title="Vegetarian" className="inline-flex items-center justify-center text-primary/80 ml-1.5">
        <Leaf className="w-3.5 h-3.5" strokeWidth={1.5} />
      </span>;
  }
  if (type === "GF") {
    return <span title="Gluten Free" className="inline-flex items-center justify-center text-muted-foreground/60 ml-1.5">
        <WheatOff className="w-3.5 h-3.5" strokeWidth={1.5} />
      </span>;
  }
  return null;
};
export default function CulinaryJourney() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="37986" id="current-expressions" ref={ref} className={cn("dark bg-background text-foreground py-20 relative overflow-hidden transition-opacity duration-1000", isVisible ? "opacity-100" : "opacity-0")}>
      {}
      <div className="absolute inset-0 pointer-events-none opacity-5 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] mix-blend-overlay" />
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_50%_0%,_var(--color-primary)_0%,_transparent_40%)] opacity-10" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-5xl md:text-7xl font-serif font-light italic tracking-tighter text-primary">
            Current Expressions
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto font-sans text-xl font-light tracking-wide">
            A seasonal exploration of the Danish landscape, where tradition meets the avant-garde in every bite.
          </p>
        </div>

        <Tabs defaultValue="forretter" className="w-full max-w-5xl mx-auto flex flex-col items-center">
          <TabsList className="bg-transparent border-b border-border/20 w-full justify-center h-auto p-0 mb-12 gap-8 rounded-none">
            {menuData.map((category, i) => <TabsTrigger key={category.id} value={category.id} data-index={i} className="bg-transparent border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-primary text-muted-foreground hover:text-foreground rounded-none px-2 py-3 text-xl font-serif italic tracking-tighter transition-all data-[state=active]:bg-transparent shadow-none">
                {category.label}
              </TabsTrigger>)}
          </TabsList>

          {menuData.map((category, i) => <TabsContent key={category.id} value={category.id} data-index={i} className="w-full animate-in fade-in slide-in-from-bottom-4 duration-700">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-10">
                {category.items.map((item, idx) => <div key={idx} data-index={idx} className="group flex flex-col gap-1">
                    <div className="flex justify-between items-baseline border-b border-border/10 pb-2 mb-2 border-dashed group-hover:border-primary/40 transition-colors">
                      <h3 className="text-2xl font-serif font-light text-foreground group-hover:text-primary transition-colors">
                        {item.name}
                      </h3>
                      <div className="flex items-center gap-2 shrink-0 ml-4">
                        <span className="text-xl font-serif text-primary font-light italic">
                          {item.price} kr.
                        </span>
                        {item.dietary && <div className="flex items-center">
                            {item.dietary.map((d, dIdx) => <DietaryIcon key={dIdx} type={d} />)}
                          </div>}
                      </div>
                    </div>
                    <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground/60 group-hover:text-muted-foreground transition-colors">
                      {item.ingredients}
                    </p>
                  </div>)}
              </div>
            </TabsContent>)}
        </Tabs>

        <div className="mt-20 text-center">
          <Button asChild size="lg" className="font-serif text-xl px-10 py-8 rounded-none border border-primary/30 hover:border-primary bg-transparent hover:bg-primary/5 text-foreground transition-all duration-500">
            <Link to="#">Secure a Table</Link>
          </Button>
        </div>
      </div>
    </section>;
}